import 'ag-grid-enterprise';
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem
} from 'ag-grid-community';
import { API_KEYWORD_SEPARATOR, client } from '@/api/backoffice-api';
import { LicenseReview } from '@/model/license-review.mjs';
import logger from '@/utils/logger.mjs';
import AppConfig from '@/utils/app-config.mjs';
import { SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE } from '@/model/Constants';
import SortModelMap from '@/utils/grid/datasource/SortModelMap';
import FilterModelMap from '@/utils/grid/datasource/FilterModelMap';
import { convertISTtoUTCtime } from '@/utils/utils';

interface MetaData {
  hasMore: boolean;
  offset: number;
  count: number;
}

interface List<T> {
  items: T[];
  meta: MetaData;
}

const multiValueSeparator = ';';
/**
 * QueueDataSource is the adapts the agGrid fetch interface to the Abuse API
 */
export default class LicenseReviewDataSource implements IServerSideDatasource {

  private sortBy: boolean = true;
  private onRefreshComplete?: () => void; 

  /**
   * Set the sortBy value
   * @param sortBy boolean value to determine if preferred=true should be added
   */
  setSortBy(sortBy: boolean): void {
    this.sortBy = sortBy;
  }

  /**
   * Set refresh completion callback
   */
  setRefreshCompleteCallback(callback: () => void): void {
    this.onRefreshComplete = callback;
  }

  /**
   * Clear the refresh callback after use
   */
  private clearRefreshCallback(): void {
    this.onRefreshComplete = undefined;
  }


  /**
   * Converts an agGrid request to an API request
   * @param params the IServerSideGetRowsRequest get rows request
   */

  async fetchData({
    startRow,
    filterModel,
    context,
    pageSize: pageSizeOverride
  }: IServerSideGetRowsRequest | any) {
    // Get regular filters
    const regularFilters = this.mapFilterModel(filterModel);
    
    // Get custom filters from context
    const customFilters: string[] = [];
    if (context?.customFilters?.isManuallyRequested?.length > 0) {
      const values = context.customFilters.isManuallyRequested.includes('isManuallyRequested') ? ['true']: ['false'];
      customFilters.push(['isManuallyRequested', 'inSet', values.join(',')].join('::'));
    }
    // Combine all filters
    const allFilters = [...regularFilters, ...customFilters];
    const hideRejection = context?.customFilters?.hideRejection
    const pageSize = pageSizeOverride ?? SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE;
    let params:any = {
      pageNumber: startRow === 0 ? 1 : Number(startRow) / pageSize + 1,
      pageSize,
      // orderBy: sortModelType && sortModelType.length > 0 ?sortModelType[0]?sortModelType: JSON.stringify([]): JSON.stringify([]),
      'filter[]': allFilters.join(','),
      hideRejection
    };

    
    // Add preferred parameter based on sortBy value
    if (this.sortBy === true) {
      params['preferred'] = 'true';
    }

    const response = await client.get(
      `${AppConfig.backOfficeApiBaseURL}/certificate-history`,
      { params }
    );
    return response.data as List<LicenseReview>;
  }

    /**
   * Map the filter model to a filter property and value
   * @param filterModel the ag-grid FilterModel object
   * @private
   */
    mapFilterModel(filterModel: FilterModel): string[] {
      logger.debug('filterModel', filterModel);
      const filterProps = Object.entries(filterModel);
      const filters: string[] = [];
      filterProps.map(([key, value]) => {
        const field = FilterModelMap.get(key) || key;
        let op;
        let val;
        switch (value.filterType) {
          case 'date': {
            op = value.type;
            value.dateTo = value.dateTo ? `${value.dateTo.split(' ')[0]} 23:59:59` : '';
            switch (value.type) {
              case 'inRange': {
                val = [convertISTtoUTCtime(value.dateFrom), convertISTtoUTCtime(value.dateTo)].join(multiValueSeparator);
                break;
              }
              case 'blank':
              case 'notBlank': {
                break;
              }
              default: {
                val = convertISTtoUTCtime(value.dateFrom);
              }
            }
            break;
          }
          case 'set': {
            if (key === 'approval_status') {
              const statusValues = (value.values || []).map((v: unknown) =>
                String(v).toLowerCase()
              );
              const hasReviewed = statusValues.includes('reviewed');
              const apiStatusValues = statusValues.filter(
                (status: string) => status !== 'reviewed'
              );

              if (hasReviewed) {
                filters.push(
                  ['isReviewedInGrid', 'inSet', 'true'].join(
                    API_KEYWORD_SEPARATOR
                  )
                );
              }

              op = 'inSet';
              val = apiStatusValues.join(multiValueSeparator);
              break;
            }
            op = 'inSet';
            val = value.values.join(multiValueSeparator);
            break;
          }
          case 'text': {
            op = value.type;
            val = value.filter.toLowerCase();
            break;
          }
          default: {
            logger.error(
              `No mapFilterModel handler for filter type ${value.filterType}`
            );
          }
        }
        if (val) filters.push([field, op, val].join(API_KEYWORD_SEPARATOR));
      });
      return filters;
    }

  /**
   * Get rows from the API
   * @param params
   */
  getRows(params: IServerSideGetRowsParams): void {
    logger.debug({ params: params.request });
    this.fetchData({...params.request,context: params.context})
      .then((result) => {
        params.success({
          rowData: result.items,
          rowCount: result.meta.count
        });
        if (this.onRefreshComplete) {
          this.onRefreshComplete();
          this.clearRefreshCallback(); // Clear after use
        }
      })
      .catch((error) => {
        logger.error('Error while fetching rows for ag-Grid', error);
        params.fail();
        this.clearRefreshCallback();
      });
  }

   /**
 * Map the agGrid sort model to our API sort model
 * @param sortModel
 * @private
 */
   mapSortModel(sortModel: SortModelItem[]): (string|null)[] {
    return sortModel.map((s) => {
      // we process 'age' differently
      if(s.colId==='0' || s.colId==='1'){
        return null;
      }

      const field = SortModelMap.get(s.colId) || s.colId;
      return [field, s.sort].join(API_KEYWORD_SEPARATOR);
    });
  }
}

interface FilterModel {
  values: unknown[];
  filterType: string;
}